// src/pages/product/ProductListPage.tsx
// Auto-generated by corebuilder. Edit freely.

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useProduct, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import { ProductCreateDialog } from '@/features/product/ProductCreateDialog';

/**
 * Product list page.
 */
export const ProductListPage = observer(() => {
  const store = useProduct();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [showCreate, setShowCreate] = useState(false);

  const fetchProducts = useCallback(() => {
    store.ListProducts({
      pagination: {
        cursor: searchParams.get('cursor') || '',
        limit: 20,
        direction: 'forward',
      },
      sort: [],
      date_range: {} as any,
      filters: {} as any,
    });
  }, [searchParams]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(search ? { search } : {});
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('product.list')}</h1>
        </div>
        <Button onClick={() => setShowCreate(true)}>{t('product.create')}</Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <Input
              placeholder={t('common.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth={false}
              className="flex-1"
            />
            <Button type="submit" variant="secondary">
              {t('common.search')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}
      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>Slug</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Tagline</TableHead>
              <TableHead>Legal Entity</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={6} message={t('product.noItems')} />
            ) : (
              store.items.map((item: any) => (
                <TableRow key={item.entity?.id || item.id || Math.random()}>
                  <TableCell>
                    {String(item.slug ?? '')}
                  </TableCell>
                  <TableCell>
                    <Link to={`/products/${item.entity?.id || item.id}`} className="text-primary hover:underline font-medium">
                      {String(item.name ?? '')}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {String(item.description ?? '')}
                  </TableCell>
                  <TableCell>
                    {String(item.tagline ?? '')}
                  </TableCell>
                  <TableCell>
                    {String(item.legal_entity ?? '')}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Link to={`/products/${item.entity?.id || item.id}`}>
                        <Button variant="ghost" size="sm">{t('common.view')}</Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => {
                          if (confirm(t('product.deleteConfirm'))) {
                            store.DeleteProduct(item.entity?.id || item.id);
                          }
                        }}
                      >
                        {t('common.delete')}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <ProductCreateDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(id) => {
          fetchProducts();
          if (id) navigate(`/products/${id}`);
        }}
      />
    </div>
  );
});

export default ProductListPage;
